import { useCallback, useMemo } from 'react';
import { Box, Button, Group, Stack, Text, Modal, ScrollArea, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams, Link } from 'react-router-dom';

import useEnsisQuery from '../../hooks/useEnsisQuery';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import { defaultModalProps } from '../../utils/mantineUtils';
import { DeleteProposal } from '../../components/Modals';
import SelectProposalSpacing from '../../components/SelectProposalSpacing';
import { formatDateFromString } from '../../utils/stringUtils';
import {
  SelectResponseFormat, CompanyDetailsForm, type CompanyDetailsFormValues, CenteredLoader
} from '../../components';
import { hasPermissionsForObject } from '../../utils/apiUtils';
import type { Proposal } from '../../types/apiTypes';
import { DefaultKeys, keyValueListToObj } from '../../utils/keyValueUtils';
import { getAddressFromString, addressToString } from '../../utils/addressUtils';

interface Props {
  proposal: Proposal
}

const ProposalDetails: React.FC<Props> = ({ proposal }: Props) => {
  const { organization } = useParams();
  const [deleteProposalOpened, deleteProposalHandlers] = useDisclosure();
  const { colors } = useMantineTheme();
  const proposalKeyValueQueryRoute = `/app/proposals/${proposal.uid}/key-value-items` as const;
  const orgKeyValueQueryRoute = `/app/organizations/${organization}/key-value-items` as const;

  const getInitialValuesFromKeyValuesObj = useCallback((
    keyValuesObj: Record<string, string>
  ) => {
    const parsedAddress = keyValuesObj[DefaultKeys.ADDRESS] === undefined
      ? undefined
      : getAddressFromString(keyValuesObj[DefaultKeys.ADDRESS]);
    return {
      address1: parsedAddress?.address1 ?? '',
      address2: parsedAddress?.address2 ?? '',
      city: parsedAddress?.city ?? '',
      state: parsedAddress?.state ?? '',
      zipCode: parsedAddress?.zipCode ?? '',
      phone: keyValuesObj[DefaultKeys.PHONE] ?? '',
      ein: keyValuesObj[DefaultKeys.EIN] ?? '',
      authorizedRep: keyValuesObj[DefaultKeys.AUTHORIZED_REP] ?? '',
      email: keyValuesObj[DefaultKeys.COMPANY_EMAIL] ?? '',
      name: keyValuesObj[DefaultKeys.COMPANY_NAME] ?? ''
    };
  }, []);

  const { data: proposalKeyValues, isLoading: proposalKeyValuesLoading } = useEnsisQuery(
    proposalKeyValueQueryRoute
  );
  const { data: orgKeyValues, isLoading: orgKeyValuesLoading } = useEnsisQuery(
    orgKeyValueQueryRoute
  );

  const placeholderValues = useMemo(
    () => getInitialValuesFromKeyValuesObj(keyValueListToObj(orgKeyValues?.items ?? [])),
    [orgKeyValues?.items]
  );
  const initialFormValues = useMemo(
    () => getInitialValuesFromKeyValuesObj(keyValueListToObj(proposalKeyValues?.items ?? [])),
    [proposalKeyValues?.items]
  );

  const editProposalKeyValueItemsMutation = useEnsisMutation(
    proposalKeyValueQueryRoute,
    {
      contentType: 'application/json',
      requestType: 'patch',
      successMessage: 'Proposal details updated',
      queryKeysToInvalidate: [proposalKeyValueQueryRoute]
    }
  );

  const handleSubmit = useCallback((values: CompanyDetailsFormValues) => {
    const { phone, email, ein, authorizedRep, name, ...addressValues } = values;

    const items = [
      {
        key: DefaultKeys.PHONE,
        value: phone
      },
      {
        key: DefaultKeys.ADDRESS,
        value: addressToString(addressValues)
      },
      {
        key: DefaultKeys.COMPANY_NAME,
        value: name
      },
      {
        key: DefaultKeys.EIN,
        value: ein
      },
      {
        key: DefaultKeys.COMPANY_EMAIL,
        value: email
      },
      {
        key: DefaultKeys.AUTHORIZED_REP,
        value: authorizedRep
      }
    ];
    editProposalKeyValueItemsMutation.mutate({ items });
  }, []);

  const canDeleteProposal = hasPermissionsForObject(proposal, 'delete');
  const canEditProposal = hasPermissionsForObject(proposal, 'change');
  const loading = orgKeyValuesLoading || proposalKeyValuesLoading;
  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <ScrollArea type='hover' flex={1}>
      <Stack align='flex-start' gap={0}>
        <Modal opened={deleteProposalOpened} {...defaultModalProps}>
          <DeleteProposal
            close={deleteProposalHandlers.close}
            proposalName={proposal.name ?? ''}
            proposalUid={proposal.uid ?? ''}
          />
        </Modal>
        <Group
          style={{ alignSelf: 'stretch' }}
          justify='flex-start'
          gap={32}
          mb={24}
        >
          <Stack gap={8}>
            <Group style={{ alignSelf: 'flex-start' }}>
              <Text fz='sm' ta='start' fw={600}> Date Created </Text>
            </Group>
            <Text> {formatDateFromString(proposal?.created_at ?? undefined) ?? 'Date created unknown'} </Text>
          </Stack>
          <Stack gap={8}>
            <Text fz='sm' ta='start' fw={600}> Last Edited </Text>
            <Text> {formatDateFromString(proposal?.created_at ?? undefined) ?? 'Last edited unknown'} </Text>
          </Stack>
          {
            canDeleteProposal && (
              <Box style={{ flex: 1, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                <Button
                  variant='subtle'
                  c={colors.red[5]}
                  onClick={deleteProposalHandlers.open}
                >
                  Delete Proposal
                </Button>
              </Box>
            )
          }
        </Group>
        <Text fz='lg' mb={16}>
          Formatting
        </Text>
        <Group mb={24}>
          <SelectResponseFormat proposal={proposal} />
          <SelectProposalSpacing
            proposalUid={proposal?.uid ?? ''}
            initialSpacing={proposal?.spacing ?? 1}
          />
        </Group>
        <Stack align='flex-start' ta='left' maw='50%' gap={8} mb={16}>
          <Text fz='lg'>
            Basic Proposal Information
          </Text>
          <Text fz='sm' c={colors.gray[7]}>
            {'This optional information will be used for generating responses. It is used for this proposal only.' +
              ' If you would like to update these fields for all future proposals, please visit '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to={`/org/${organization}/settings/company-details`}
            >
              Settings
            </Link>
          </Text>
        </Stack>
        <CompanyDetailsForm
          style={{ textAlign: 'left' }}
          initialValues={initialFormValues}
          placeholderValues={placeholderValues}
          onSubmit={handleSubmit}
          canEdit={canEditProposal}
          loading={editProposalKeyValueItemsMutation.isPending}
        />
      </Stack>
    </ScrollArea>
  );
};

export default ProposalDetails;
